import { CheckOutlined, InfoCircleOutlined, LeftOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DatePicker, Input, message, Select, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getSales } from "@/modules/sales/services/sales.backend";
import {
	type FrontendDiscountTier,
	getDiscountTiers,
} from "../services/early-payment-discounts.api";
import { getAllPaymentSchedules } from "../services/payment-schedules.api";
import { createPayment, getPaymentApiErrorMessage } from "../services/payments.api";
import type { PaymentMethod } from "../types/payments.types";
import {
	buildReceipt,
	formatUzs,
	getDiscountPct,
	mergeSalesWithPaymentSchedules,
	paymentMethodLabels,
} from "../utils/payments.utils";

export function EarlyPaymentPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const queryClient = useQueryClient();
	const [discountTiers, setDiscountTiers] = useState<FrontendDiscountTier[]>([]);

	useEffect(() => {
		getDiscountTiers()
			.then(setDiscountTiers)
			.catch((err) => {
				console.error("Failed to load discount tiers:", err);
				message.error("Chegirma shkalasini yuklashda xatolik yuz berdi");
			});
	}, []);
	const salesQuery = useQuery({
		queryKey: ["sales", "payments-early"],
		queryFn: () => getSales({ limit: 100, page: 1 }),
		staleTime: 0,
		refetchOnMount: "always",
	});
	const paymentSchedulesQuery = useQuery({
		queryKey: ["payment-schedules", "payments-early"],
		queryFn: () => getAllPaymentSchedules(),
		staleTime: 0,
		refetchOnMount: "always",
	});
	const sales = salesQuery.data?.items ?? [];
	const salesWithSchedules = useMemo(
		() => mergeSalesWithPaymentSchedules(sales, paymentSchedulesQuery.data?.data ?? []),
		[sales, paymentSchedulesQuery.data?.data]
	);
	const installmentSales = useMemo(
		() =>
			salesWithSchedules.filter(
				(sale) =>
					sale.paymentType === "muddatli" &&
					sale.status === "faol" &&
					sale.schedule.some((item) => item.status !== "paid")
			),
		[salesWithSchedules]
	);
	const requestedSaleId = searchParams.get("saleId");
	const returnClientId = searchParams.get("clientId");
	const firstInstallmentSale =
		installmentSales.find((sale) => sale.id === requestedSaleId) ?? installmentSales[0];
	const returnPath = returnClientId ? `/clients/${returnClientId}` : "/payments";

	const [saleId, setSaleId] = useState(firstInstallmentSale?.id ?? "");
	const [monthsCount, setMonthsCount] = useState(6);
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("bank");
	const [paymentDate, setPaymentDate] = useState(dayjs().format("YYYY-MM-DD"));
	const [receiptNumber, setReceiptNumber] = useState(buildReceipt("CH-EARLY"));

	const selectedSale = installmentSales.find((sale) => sale.id === saleId);
	const unpaidRows = selectedSale?.schedule.filter((item) => item.status !== "paid") ?? [];
	const rowsToPay = unpaidRows.slice(0, monthsCount);
	const subtotal = rowsToPay.reduce((sum, item) => sum + item.plannedAmount, 0);
	const discountPct = getDiscountPct(monthsCount, discountTiers);
	const discountAmountUzs = Math.round(subtotal * (discountPct / 100));
	const totalToPayUzs = Math.round(subtotal - discountAmountUzs);
	const createPaymentMutation = useMutation({
		mutationFn: createPayment,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["payments"] });
			queryClient.invalidateQueries({ queryKey: ["sales"] });
			if (selectedSale) {
				queryClient.invalidateQueries({ queryKey: ["sale", selectedSale.id] });
			}
		},
	});

	useEffect(() => {
		if (saleId || !firstInstallmentSale) {
			return;
		}
		setSaleId(firstInstallmentSale.id);
	}, [firstInstallmentSale, saleId]);

	useEffect(() => {
		if (unpaidRows.length > 0 && monthsCount > unpaidRows.length) {
			setMonthsCount(Math.min(6, unpaidRows.length));
		}
	}, [monthsCount, unpaidRows.length]);

	const handleSaleChange = (nextSaleId: string) => {
		setSaleId(nextSaleId);
		setReceiptNumber(buildReceipt("CH-EARLY"));
	};

	const handleSubmit = async () => {
		if (!selectedSale) {
			message.warning("Sotuvni tanlang");
			return;
		}
		if (rowsToPay.length < monthsCount) {
			message.error("To'lanmagan grafik qatorlari yetarli emas");
			return;
		}
		if (monthsCount < 3) {
			message.error("Erta to'lov kamida 3 oy uchun bo'lishi kerak");
			return;
		}
		const scheduleIds = rowsToPay.map((row) => row.id).filter((id): id is string => Boolean(id));
		if (scheduleIds.length !== rowsToPay.length) {
			message.error("Backend grafik qatorlari id qaytarmayapti. Erta to'lov yuborib bo'lmaydi.");
			return;
		}

		const paymentPayload = {
			saleId: selectedSale.id,
			amountUzs: totalToPayUzs,
			discountAmountUzs,
			monthsPaid: rowsToPay.length,
			paymentMethod,
			paymentDate,
			receiptNumber,
			note: `${rowsToPay.length} oy uchun erta to'lov`,
			scheduleIds,
		};

		try {
			await createPaymentMutation.mutateAsync(paymentPayload);
			message.success(`${rowsToPay.length} oy yopildi, ${discountPct}% skidka qo'llandi`);
			navigate(returnPath, { state: location.state });
		} catch (error: unknown) {
			message.error(getPaymentApiErrorMessage(error, "Erta to'lovni saqlashda xatolik yuz berdi"));
		}
	};

	return (
		<div className="crm-payment-form-page">
			<div className="crm-page-head">
				<div>
					<Button
						className="crm-back-link"
						icon={<LeftOutlined />}
						onClick={() => navigate(returnPath, { state: location.state })}
					>
						{returnClientId ? "Mijoz kartochkasi" : "To'lovlar"}
					</Button>
					<h1>Erta to'lov</h1>
					<p>Bir nechta oyni oldindan yopish va chegirma hisoblash</p>
				</div>
				<Tag color="green">{discountPct}% skidka</Tag>
			</div>

			<div className="crm-payment-form-grid">
				<div className="crm-card">
					<div className="crm-card-head">
						<h3>Necha oy oldindan?</h3>
					</div>
					<div className="crm-card-body">
						{returnClientId ? null : (
							<div className="crm-sale-field">
								<span>Sotuv / mijoz</span>
								<Select
									value={saleId}
									onChange={handleSaleChange}
									loading={salesQuery.isLoading || paymentSchedulesQuery.isLoading}
									options={installmentSales.map((sale) => ({
										label: `${sale.clientName} - ${sale.buildingName}, ${sale.roomNumber}-xona - ${formatUzs(sale.monthlyPayment)}/oy`,
										value: sale.id,
									}))}
								/>
							</div>
						)}

						<div className="crm-sale-field">
							<span>Oylar soni</span>
							{unpaidRows.length === 0 ? (
								<span className="crm-empty-hint" style={{ color: "var(--crm-text-secondary)" }}>
									{paymentSchedulesQuery.isLoading
										? "Yuklanmoqda..."
										: "Grafik qatorlari topilmadi"}
								</span>
							) : (
								<Select
									value={monthsCount}
									onChange={setMonthsCount}
									options={[
										{ label: "3 oy", value: 3, disabled: unpaidRows.length < 3 },
										{ label: "6 oy", value: 6, disabled: unpaidRows.length < 6 },
										{ label: "12 oy", value: 12, disabled: unpaidRows.length < 12 },
									]}
								/>
							)}
						</div>

						<div className="crm-payment-two-col">
							<div className="crm-sale-field">
								<span>Usul</span>
								<Select
									value={paymentMethod}
									onChange={setPaymentMethod}
									options={(Object.keys(paymentMethodLabels) as PaymentMethod[]).map((method) => ({
										label: paymentMethodLabels[method],
										value: method,
									}))}
								/>
							</div>
							<div className="crm-sale-field">
								<span>Sana</span>
								<DatePicker
									value={dayjs(paymentDate)}
									onChange={(_, value) => {
										if (!Array.isArray(value) && value) {
											setPaymentDate(value);
										}
									}}
								/>
							</div>
						</div>

						<div className="crm-sale-field">
							<span>Chek raqami</span>
							<Input
								value={receiptNumber}
								onChange={(event) => setReceiptNumber(event.target.value)}
							/>
						</div>

						<div className="crm-form-actions">
							<Button onClick={() => navigate(returnPath, { state: location.state })}>Bekor</Button>
							<Button
								className="crm-primary-btn"
								type="primary"
								icon={<CheckOutlined />}
								onClick={handleSubmit}
								loading={createPaymentMutation.isPending}
							>
								To'lashni yakunlash
							</Button>
						</div>
					</div>
				</div>

				<div className="crm-payment-side">
					<div className="crm-card">
						<div className="crm-card-head">
							<h3>Skidka hisobi</h3>
							<Tag color="green">
								{discountPct}% -{" "}
								{monthsCount >= 12 ? "12+ oy" : monthsCount >= 6 ? "6-11 oy" : "3-5 oy"}
							</Tag>
						</div>
						<div className="crm-card-body">
							<div className="crm-kv">
								<span>
									{rowsToPay.length} x oylik
									{selectedSale ? ` (${formatUzs(selectedSale.monthlyPayment)})` : ""}
								</span>
								<b>{formatUzs(subtotal)}</b>
							</div>
							<div className="crm-kv">
								<span>Skidka summasi</span>
								<b>-{formatUzs(discountAmountUzs)}</b>
							</div>
							<div className="crm-kv crm-payment-total">
								<span>To'lanadi</span>
								<b>{formatUzs(totalToPayUzs)}</b>
							</div>
						</div>
					</div>

					<div className="crm-payment-rule">
						<InfoCircleOutlined />
						<span>
							{rowsToPay.length} ta grafik qatori "to'landi" bo'ladi, discount_amount=
							{formatUzs(discountAmountUzs)}, months_paid={rowsToPay.length}. Shkala - Skidka
							shkalasi sahifasida.
						</span>
					</div>
				</div>
			</div>
		</div>
	);
}

export default EarlyPaymentPage;
